import { IApiError } from '@frontend/request';

import { copyTradeAPIController } from '@aqx-core/common/api/copy-trade/controller';
import { i18n } from '@aqx-core/common/i18n';
import { ECommonResponseCode, mapResponseCodeToMessage } from '@aqx-core/common/request';
import { copyTradeAssetService } from '@aqx-core/common/services/copy-trade/asset/service';
import { copyTradeViewController } from '@aqx-core/common/services/copy-trade/controller';
import { copyTradeService } from '@aqx-core/common/services/copy-trade/service';
import { orderListingViewController } from '@aqx-core/common/services/order/controller';
import { EUISnackBarMessageType, snackbarController } from '@aqx-core/common/services/snackbar';

import {
  ICloseCopyTradeOrderParams,
  IEditCopyTradeOrderParams,
  IGetCopyTradeOrderParams,
  IGetCopyTradeProfileParams
} from './types';

class CopyTradeAssetViewController {
  public getCopyTradeProfile = async ({
    copierId,
    timeframe = copyTradeService.context.returnHistoryChartTimeframe,
    isCustomLoading,
    onFinally
  }: IGetCopyTradeProfileParams) => {
    if (!isCustomLoading) {
      copyTradeAssetService.setIsLoadingCopyTradeProfile(true);
      copyTradeService.setSelectedStrategy(null);
      copyTradeAssetService.setCopyTradeProfile(null);
    }
    await copyTradeAPIController.getCopyTradeProfile(
      { copierId, timeframe },
      {
        onSuccess: (result) => {
          copyTradeAssetService.setCopyTradeProfile(result ?? null);
          copyTradeAssetService.setIsLoadingCopyTradeProfile(false);
        },
        onError: async (error) => {
          const { code } = error as IApiError;
          if (code === ECommonResponseCode.PW_1005) {
            await copyTradeViewController.getUserInfo({});
          }
          copyTradeAssetService.setIsLoadingCopyTradeProfile(false);
        },
        onFinally: () => {
          onFinally?.();
          copyTradeAssetService.setIsLoadingCopyTradeProfile(false);
        }
      }
    );
  };

  public getCopyTradeProfileImage = async (copierId: string) => {
    copyTradeAssetService.setIsLoadingCopyTradeProfileImage(true);
    await copyTradeAPIController.getCopyTradeProfileImage(
      { copierId },
      {
        onSuccess: (result) => {
          copyTradeAssetService.setCopyTradeProfileImage(result ?? undefined);
          copyTradeAssetService.setIsLoadingCopyTradeProfileImage(false);
        },
        onError: () => {
          copyTradeAssetService.setIsLoadingCopyTradeProfileImage(false);
        }
      }
    );
  };

  public getCopierMissedTrades = async (copierId: string) => {
    copyTradeAssetService.setIsLoadingCopyTradeMissedTrades(true);
    await copyTradeAPIController.getCopierMissedTrades(
      { copierId },
      {
        onSuccess: (result) => {
          copyTradeAssetService.setCopyTradeMissedTrades(result ?? []);
          copyTradeAssetService.setIsLoadingCopyTradeMissedTrades(false);
        },
        onError: (error) => {
          const { code } = error as IApiError;
          if (code === ECommonResponseCode.PW_1005) {
            copyTradeViewController.getUserInfo({});
          }
          copyTradeAssetService.setIsLoadingCopyTradeMissedTrades(false);
        }
      }
    );
  };

  public getCopyTradeOrder = async ({ copierId, copiedSignalId, onSuccess, onError }: IGetCopyTradeOrderParams) => {
    copyTradeAssetService.setIsLoadingGetCopyTradeOrder(true);
    copyTradeAssetService.setCopyTradeOrder(null);

    await copyTradeAPIController.getCopyTradeOrder(
      { copierId, copiedSignalId },
      {
        onSuccess: (result) => {
          onSuccess?.();
          copyTradeAssetService.setCopyTradeOrder(result ?? null);
          copyTradeAssetService.setIsLoadingGetCopyTradeOrder(false);
        },
        onError: (error) => {
          const { code } = error as IApiError;
          onError?.(code);
          copyTradeAssetService.setIsLoadingGetCopyTradeOrder(false);
        }
      }
    );
  };

  public editCopyTradeOrder = async ({ values, onSuccess, onError }: IEditCopyTradeOrderParams) => {
    copyTradeAssetService.setIsLoadingEditCopyTradeOrder(true);
    await copyTradeAPIController.editCopyTradeOrder(
      {
        copierId: values.copierId,
        copiedSignalId: values.copiedSignalId,
        stopPrice: values.stopPrice,
        limitPrice: values.limitPrice
      },
      {
        onSuccess: () => {
          onSuccess?.();
          // refetch open positions
          orderListingViewController.handleRefetchOrderRelatedData();
          copyTradeAssetService.setIsLoadingEditCopyTradeOrder(false);
        },
        onError: (error) => {
          const { code } = error as IApiError;
          snackbarController.showAlert({
            message: i18n.t('Failed to update position.'),
            description: mapResponseCodeToMessage(code, i18n.t('Please try again.')),
            messageType: EUISnackBarMessageType.ERROR
          });

          onError?.(code);
          orderListingViewController.handleRefetchOrderRelatedData();
          copyTradeAssetService.setIsLoadingEditCopyTradeOrder(false);
        }
      }
    );
  };

  public closeCopyTradeOrder = async ({ values, onSuccess, onError }: ICloseCopyTradeOrderParams) => {
    copyTradeAssetService.setIsLoadingCloseCopyTradeOrder(true);
    await copyTradeAPIController.closeCopyTradeOrder(
      { copierId: values.copierId, copiedSignalId: values.copiedSignalId },
      {
        onSuccess: () => {
          onSuccess?.();
          snackbarController.showAlert({
            message: i18n.t('Position has been closed.'),
            messageType: EUISnackBarMessageType.SUCCESS
          });
          // refetch open positions
          orderListingViewController.handleRefetchOrderRelatedData();
          copyTradeAssetService.setIsLoadingCloseCopyTradeOrder(false);
        },
        onError: (error) => {
          const { code } = error as IApiError;
          snackbarController.showAlert({
            message: mapResponseCodeToMessage(code, i18n.t('Failed to close position.')),
            messageType: EUISnackBarMessageType.ERROR
          });
          if (code === ECommonResponseCode.PW_1003) {
            copyTradeViewController.reset();
          }
          onError?.(code);
          orderListingViewController.handleRefetchOrderRelatedData();
          copyTradeAssetService.setIsLoadingCloseCopyTradeOrder(false);
        }
      }
    );
  };
}

export const copyTradeAssetViewController = new CopyTradeAssetViewController();
